import React, { useState, useEffect } from 'react'
import { Spinner as NuiSpinner } from '@theneatcompany/nui'

import { NEAT_DATA_URL } from '@/config'
import { Text } from '@/components/common'
import { Layout, Row, Rows, Section, Column } from '@/components/layout'
import WebinarItem from '@/components/sections/WebinarItem'
import TestimonialSliderSlim from '@/components/blocks/TestimonialSliderSlim'
import MailingListOptInForm from '@/components/Pages/EmailPreferences/MailingListOptInForm'

import styles from './webinar.module.scss'

const pageId = 'webinars'

const customTitles = {
  'Live Replay': 'Webinar Replays',
}

const MAX_UPCOMING_WEBINARS = 3

export default function Webinars() {
  const [webinarsData, setWebinarsData] = useState([])
  const [presentersData, setPresentersData] = useState([])
  const [loading, setLoading] = useState(true)

  const filteredWebinarsData = webinarsData.reduce((acc, curr) => {
    const isUpcoming = ['Live', 'Live Replay'].includes(curr.type) && new Date(curr.date) > new Date()
    if (isUpcoming) {
      acc.Upcoming.push(curr)
    } else {
      acc[curr.type]?.push(curr)
    }
    return acc
  }, { Upcoming: [], 'Getting Started': [], 'Live Replay': [], Snippets: [] })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${NEAT_DATA_URL}/webinars/webinars.json`)
        const { webinars = [], presenters = [] } = await response.json()

        // Fetch presenters data
        setPresentersData(presenters)

        setWebinarsData(webinars)
        setLoading(false)
      } catch (error) {
        console.error('Error fetching JSON data:', error)
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const renderCategoryButton = (sectionId) => {
    if (sectionId === 'Upcoming' || !filteredWebinarsData[sectionId]?.length) return null

    return (
      <button key={sectionId} className={styles.scrollToBtn} onClick={() => scrollToSection(sectionId)}>
        {customTitles[sectionId] || sectionId}
      </button>
    )
  }

  const findPresentersById = (presenters, webinar) => {
    if (Array.isArray(webinar.presenterIds)) {
      return presenters.filter((presenter) => webinar.presenterIds.includes(presenter.id))
    }

    const thePresenter = presenters.find((presenter) => presenter.id === webinar.presenterId)
    return thePresenter ? [thePresenter] : []
  }

  const testimonials = [
    {
      text: 'I have been attending the webinars offered by Neat and it has given me a better understanding of how to utilize Neat to make my agency work more efficiently.',
      author: 'Neat Customer - Diana S.',
    },
    {
      text: 'It was GREAT and very helpful, especially the handouts!',
      author: 'Neat Customer - Bill G.',
    },
    {
      text: 'Each training has been something I’ve learned something I wasn’t aware Neat could do.',
      author: 'Neat Customer - Craig C.',
    },
    {
      text: 'All good, way informative. Picked up some excellent tips.',
      author: 'Neat Customer - James N.',
    },
    {
      text: 'Overall an excellent webinar.  Love the handouts and having moderators that can answer questions quickly in the chat.',
      author: 'Neat Customer - Edward V.',
    },
    {
      text: 'There was a lot of information shared. I have been using neat for years now. But I learned so much at this training. There is so much more to this program than I haven\'t been using.',
      author: 'Neat Customer - Kim P.',
    },
  ]

  const renderWebinarSection = (webinarType) => {
    const webinars = filteredWebinarsData[webinarType]
    if (webinarType === 'Upcoming' || !webinars?.length) return null

    return (
      <Section key={webinarType} className={styles.webinarType} id={webinarType}>
        <Row>
          <Text.H2 brandPrimary>{customTitles[webinarType] || webinarType}</Text.H2>
        </Row>
        <Rows className={styles.webinarList}>
          {webinars.map((webinar) => (
            <Row key={webinar.name + webinar.date}>
              <WebinarItem
                presenters={findPresentersById(presentersData, webinar)}
                webinarImg={webinar.icon}
                webinarTitle={webinar.name}
                webinarUrl={webinar.registerUrl}
              />
            </Row>
          ))}
        </Rows>

        <button className={styles.scrollToBtn} onClick={() => scrollToSection('categories')}>
          Back To Menu
        </button>
      </Section>
    )
  }

  const renderUpcomingWebinarSection = () => {
    const visibleUpcomingWebinars = filteredWebinarsData.Upcoming.slice(0, MAX_UPCOMING_WEBINARS)
    if (!visibleUpcomingWebinars.length) return null

    let size = 50
    if (visibleUpcomingWebinars.length === 2) {
      size = 45
    } else if (visibleUpcomingWebinars.length === 3) {
      size = 30
    }

    return (
      <Section className={styles.upcomingWebinar} paddingBottom={0}>
        <Row>
          <Text.H2 brandPrimary>Upcoming Webinar{filteredWebinarsData.Upcoming.length > 1 ? 's' : ''}</Text.H2>
        </Row>
        <Row className={styles.upcomingWebinarList}>
          {visibleUpcomingWebinars.map((webinar) => (
            <Column
              key={webinar.name + webinar.date}
              center
              className={styles.upcomingCard}
              size={size}
            >
              <WebinarItem
                isUpcoming
                presenters={findPresentersById(presentersData, webinar)}
                webinarBtnText="Register Now"
                webinarImg={webinar.icon}
                webinarTitle={webinar.name}
                webinarUrl={webinar.registerUrl}
              />
            </Column>
          ))}
        </Row>
      </Section>
    )
  }

  return (
    <Layout.Content>
      <Section bgForeground>
        <Column className={styles.headerDoubleColumn}>
          <Row className={styles.headerText}>
            <Text.H1 bold brandSecondary normal>
              Neat Webinar Replays
            </Text.H1>
            <Text.P bold noMargin onTint size="small" type="display">
              Access on-demand Neat webinars for exclusive tips and tricks, unlocking the full potential of Neat.
            </Text.P>

            <Text.P bold brandPrimary noMargin size="small" style={{ marginTop: '20px' }}>
              See what attendees are saying:
            </Text.P>
            <TestimonialSliderSlim slides={testimonials} />
          </Row>
          <Row className={styles.headerForm}>
            <div className="dark-background">
              <Text.P bold onTint>Signup for Upcoming Webinars</Text.P>
              <MailingListOptInForm buttonMessageId="cta.getNotified" buttonVariant="primary" emailOnly />
            </div>
          </Row>
        </Column>
      </Section>

      {loading && <Section><NuiSpinner /></Section>}
      {!loading && (
        <div>
          <Section className={styles.webinarCategoriesWrap} id="categories" paddingBottom={0}>
            <Row className={styles.webinarCategories}>
              <div>Webinar Categories</div>
              {Object.keys(filteredWebinarsData).map(renderCategoryButton)}
            </Row>
          </Section>
          {renderUpcomingWebinarSection()}
          <div className={styles.webinarTypeSections}>
            {Object.keys(filteredWebinarsData).map(renderWebinarSection)}
          </div>
        </div>
      )}
    </Layout.Content>
  )
}

Webinars.layoutProps = {
  pageId,
}
